// I due menù dell'offerta (spec § 7.2, § 8): «PCV» con tutte le quote in vendita, «Spread» con i soli spread che
// esistono con la quota scelta. Così ogni coppia scelta è un'offerta vera.
//
// Quota e spread arrivano come stringhe decimali in forme diverse («20.00», «20»; «0.0350», «0.035»): si confrontano
// come numeri letti dalla stringa, senza conti. `Number("0.0350") === Number("0.035")` è esatto (stesso double);
// l'errore del float nasce solo dall'aritmetica, che qui non c'è.
import type { OffertaMenu } from "@/api/preventivo";

interface Voce {
  offerta: OffertaMenu;
  quota: number;
  spread: number;
}

/** Le offerte che possono stare nei menù: in vendita, con quota e spread scritti e leggibili come numero. Una con
 *  `scaduta_il` è solo la scelta di un condominio che non è più in vendita: mai in un menù (spec 2026-10-28 § 3c). */
function voci(offerte: OffertaMenu[]): Voce[] {
  const fuori: Voce[] = [];
  for (const o of offerte) {
    if (o.scaduta_il) continue;
    if (o.quota === null || o.spread === null) continue;
    const quota = Number(o.quota);
    const spread = Number(o.spread);
    if (Number.isFinite(quota) && Number.isFinite(spread)) fuori.push({ offerta: o, quota, spread });
  }
  return fuori;
}

const distintiCrescenti = (xs: number[]) => [...new Set(xs)].sort((a, b) => a - b);

/** Le quote fisse in vendita, distinte e crescenti. */
export function quote(offerte: OffertaMenu[]): number[] {
  return distintiCrescenti(voci(offerte).map((v) => v.quota));
}

/** Gli spread che esistono con questa quota, distinti e crescenti. */
export function spreadPer(offerte: OffertaMenu[], quota: number): number[] {
  return distintiCrescenti(voci(offerte).filter((v) => v.quota === quota).map((v) => v.spread));
}

/** L'offerta con questa quota e questo spread. Con due fornitori sulla stessa coppia (oggi non succede) prende la
 *  prima: il menù del fornitore si costruisce quando il caso si presenta, con un'etichetta approvata (spec § 7.2). */
export function trova(offerte: OffertaMenu[], quota: number, spread: number): OffertaMenu | undefined {
  return voci(offerte).find((v) => v.quota === quota && v.spread === spread)?.offerta;
}

/** Cambiando la quota lo spread resta, se esiste con la nuova quota; altrimenti passa al primo disponibile. */
export function cambiaQuota(offerte: OffertaMenu[], attuale: OffertaMenu | undefined, quota: number): OffertaMenu {
  const spread = attuale?.spread == null ? undefined : Number(attuale.spread);
  const tenuta = spread === undefined ? undefined : trova(offerte, quota, spread);
  if (tenuta) return tenuta;
  const primo = spreadPer(offerte, quota)[0];
  const offerta = primo === undefined ? undefined : trova(offerte, quota, primo);
  if (!offerta) throw new Error(`nessuna offerta con quota ${quota}`);
  return offerta;
}
